import React, { useMemo, useCallback } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import {
  Inventory as InventoryIcon,
  AttachMoney as AttachMoneyIcon,
  Warehouse as WarehouseIcon,
  Store as StoreIcon,
  LocalShipping as LocalShippingIcon,
  CheckCircleOutline,
  HighlightOff,
  HelpOutline,
  AccessTime,
  ShoppingBag as ShoppingBagIcon,
  Apps as AppsIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { StatusSummary } from '../../../api/models/StatusSummary';
import { INV_AVAILABILITY_WRITE_OFF_LABEL_PLURAL } from '../../../constants/inventory-status-labels';
import { useInventoryStats } from './InventoryStats.hooks';
import {
  StatsContainer,
  StatItem,
  IconContainer,
  StatInfo,
  StatLabel,
  StatValue,
  getInventoryStatsIconSx,
  AvailabilityStatsContainer,
  AvailabilityStatItem,
} from './InventoryStats.styles';
import { useInventoryWarehouseKPIPermissions } from '../../../hooks/useInventoryWarehouseKPIPermissions';
import { useInventoryGeneralKPIPermissions } from '../../../hooks/useInventoryGeneralKPIPermissions';
import { useInventoryBranchKPIPermissions } from '../../../hooks/useInventoryBranchKPIPermissions';
import { formatCurrency } from '../../../utils/formatters/money';
import {
  INV_STATUS_BRANCH_LABEL,
  INV_STATUS_WAREHOUSE_LABEL,
} from '../../../constants/inventory-status-labels';
import { nextStatusFilterValue } from '../../common/StatusSummaryBar';

export interface InventoryStatsProps {
  totalProducts: number;
  totalValue: number;
  totalCostValue?: number;
  writeOffCount?: number;
  statusSummary?: StatusSummary[];
  lastActivityDate: string;
  variant?: 'warehouse' | 'branch';
  availabilityStatusSummary?: { status: string; count: number }[];
  /** Menos margen inferior (p. ej. gestión en almacén junto a filtros). */
  compactSpacing?: boolean;
  /** Perfil de permisos KPI según menú (almacén, sucursal o inventario general). */
  kpiPermissionProfile?: 'warehouse' | 'branch' | 'general';
  /** Total de filas en catálogo de productos (solo con perfil general + permiso KPI). */
  catalogProductTotal?: number;
  /**
   * When set, filterable KPIs become clickable (hover only; chip shows active filter).
   * Empty string clears the status filter (Total).
   */
  activeFilterValue?: string;
  onMetricClick?: (filterValue: string) => void;
}

const InventoryStats: React.FC<InventoryStatsProps> = ({
  totalProducts,
  totalValue,
  totalCostValue = 0,
  writeOffCount = 0,
  statusSummary = [],
  lastActivityDate,
  variant = 'warehouse',
  availabilityStatusSummary = [],
  compactSpacing = false,
  kpiPermissionProfile = 'warehouse',
  catalogProductTotal,
  activeFilterValue = '',
  onMetricClick,
}) => {
  const {
    assignedProducts,
    unassignedProducts,
    inTransitProducts,
    allAvailabilityStatuses,
    availabilityStatusMap,
    getAvailabilityIcon,
    getAvailabilityStatusName,
  } = useInventoryStats({
    totalProducts,
    totalValue,
    totalCostValue,
    writeOffCount,
    statusSummary,
    lastActivityDate,
    variant,
    availabilityStatusSummary,
  });

  const warehouseKpiPerms = useInventoryWarehouseKPIPermissions();
  const generalKpiPerms = useInventoryGeneralKPIPermissions();
  const branchKpiPerms = useInventoryBranchKPIPermissions();
  const kpiPerms =
    kpiPermissionProfile === 'general'
      ? generalKpiPerms
      : kpiPermissionProfile === 'branch'
        ? branchKpiPerms
        : warehouseKpiPerms;
  const theme = useTheme();
  const iconSx = useMemo(() => getInventoryStatsIconSx(theme), [theme]);
  const clickable = onMetricClick != null;
  const enableFilterClicks = clickable && kpiPermissionProfile !== 'general';

  const handleMetricClick = useCallback(
    (filterValue: string) => {
      if (onMetricClick == null) return;
      onMetricClick(nextStatusFilterValue(activeFilterValue, filterValue));
    },
    [activeFilterValue, onMetricClick],
  );

  const clickableSx = useMemo(
    () =>
      enableFilterClicks
        ? {
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            borderRadius: 1,
            '&:hover': {
              backgroundColor: alpha(
                theme.palette.primary.main,
                theme.palette.mode === 'dark' ? 0.12 : 0.04,
              ),
            },
          }
        : undefined,
    [enableFilterClicks, theme],
  );

  const metricProps = (filterValue: string) =>
    enableFilterClicks
      ? {
          role: 'button' as const,
          tabIndex: 0,
          'aria-pressed':
            filterValue === '' ? activeFilterValue === '' : activeFilterValue === filterValue,
          onClick: () => handleMetricClick(filterValue),
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleMetricClick(filterValue);
            }
          },
          sx: clickableSx,
        }
      : {};

  const isGeneralBranchLayout = kpiPermissionProfile === 'general' && variant === 'branch';

  const hasPlacementKpi =
    kpiPerms.canKpiUnassigned ||
    kpiPerms.canKpiAssigned ||
    kpiPerms.canKpiInTransit ||
    kpiPerms.canKpiBajas;

  const renderAvailabilityIcon = (status: string) => {
    const iconInfo = getAvailabilityIcon(status);

    switch (iconInfo.icon) {
      case 'CheckCircleOutline':
        return <CheckCircleOutline sx={{ color: iconInfo.color }} />;
      case 'HighlightOff':
        return <HighlightOff sx={{ color: iconInfo.color }} />;
      case 'ShoppingBag':
        return <ShoppingBagIcon sx={{ color: iconInfo.color }} />;
      case 'Store':
        return <StoreIcon sx={{ color: iconInfo.color }} />;
      case 'Lock':
        return <LockIcon sx={{ color: iconInfo.color }} />;
      case 'AccessTime':
        return <AccessTime sx={{ color: iconInfo.color }} />;
      default:
        return <HelpOutline sx={{ color: iconInfo.color }} />;
    }
  };

  /** Ubicación física: almacén → sucursal → tránsito → bajas (orden fila 2 inventario general). */
  const renderPlacementKpis = () => (
    <>
      {kpiPerms.canKpiUnassigned && (
        <StatItem
          data-compact
          {...(kpiPermissionProfile === 'warehouse' ? metricProps('UNASSIGNED') : {})}
        >
          <IconContainer sx={iconSx.unassigned} iconSize="sm">
            <WarehouseIcon />
          </IconContainer>
          <StatInfo>
            <StatLabel>{INV_STATUS_WAREHOUSE_LABEL}</StatLabel>
            <StatValue valueSize="sm">{unassignedProducts}</StatValue>
          </StatInfo>
        </StatItem>
      )}
      {kpiPerms.canKpiAssigned && (
        <StatItem data-compact>
          <IconContainer sx={iconSx.assigned} iconSize="sm">
            <StoreIcon />
          </IconContainer>
          <StatInfo>
            <StatLabel>{INV_STATUS_BRANCH_LABEL}</StatLabel>
            <StatValue valueSize="sm">{assignedProducts}</StatValue>
          </StatInfo>
        </StatItem>
      )}
      {kpiPerms.canKpiInTransit && (
        <StatItem
          data-compact
          {...(kpiPermissionProfile === 'warehouse' ? metricProps('IN_TRANSIT') : {})}
        >
          <IconContainer sx={iconSx.inTransit} iconSize="sm">
            <LocalShippingIcon />
          </IconContainer>
          <StatInfo>
            <StatLabel>En Tránsito</StatLabel>
            <StatValue valueSize="sm">{inTransitProducts}</StatValue>
          </StatInfo>
        </StatItem>
      )}
      {kpiPerms.canKpiBajas && (
        <StatItem
          data-compact
          {...(kpiPermissionProfile === 'branch' ? metricProps('CANCELLED') : {})}
        >
          <IconContainer sx={iconSx.availability} iconSize="sm">
            <HighlightOff sx={{ color: theme.palette.error.main }} />
          </IconContainer>
          <StatInfo>
            <StatLabel>{INV_AVAILABILITY_WRITE_OFF_LABEL_PLURAL}</StatLabel>
            <StatValue valueSize="sm">{writeOffCount}</StatValue>
          </StatInfo>
        </StatItem>
      )}
    </>
  );

  const availabilityBlock = (
    <AvailabilityStatsContainer
      sx={
        isGeneralBranchLayout && hasPlacementKpi
          ? {
              flex: '2 1 280px',
              minWidth: 0,
              borderLeft: { md: `1px solid ${theme.palette.divider}` },
              pl: { md: 2 },
              ml: { md: 1 },
            }
          : undefined
      }
    >
      {allAvailabilityStatuses.map((status) => (
        <AvailabilityStatItem
          key={status}
          {...(kpiPermissionProfile === 'branch' ? metricProps(status) : {})}
        >
          <IconContainer sx={iconSx.availability} iconSize="sm">
            {renderAvailabilityIcon(status)}
          </IconContainer>
          <StatInfo>
            <StatLabel sx={{ fontSize: '0.8125rem' }}>
              {getAvailabilityStatusName(status)}
            </StatLabel>
            <StatValue valueSize="sm">{availabilityStatusMap.get(status) || 0}</StatValue>
          </StatInfo>
        </AvailabilityStatItem>
      ))}
    </AvailabilityStatsContainer>
  );

  return (
    <StatsContainer
      sx={{
        ...(compactSpacing
          ? {
              marginBottom: theme.spacing(1),
            }
          : {}),
        ...(isGeneralBranchLayout
          ? {
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 0,
            }
          : {}),
      }}
    >
      {isGeneralBranchLayout ? (
        <>
          {/* Fila 1: totales y valores */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              width: '100%',
              alignItems: 'stretch',
            }}
          >
            {kpiPerms.canKpiTotalProducts && (
              <StatItem data-compact>
                <IconContainer sx={iconSx.inventory} iconSize="sm">
                  <InventoryIcon />
                </IconContainer>
                <StatInfo>
                  <StatLabel>Total piezas</StatLabel>
                  <StatValue valueSize="sm">{totalProducts}</StatValue>
                </StatInfo>
              </StatItem>
            )}

            {generalKpiPerms.canKpiCatalogProductTotal && catalogProductTotal !== undefined && (
              <StatItem data-compact>
                <IconContainer sx={iconSx.inventory} iconSize="sm">
                  <AppsIcon />
                </IconContainer>
                <StatInfo>
                  <StatLabel>Total Productos</StatLabel>
                  <StatValue valueSize="sm">{catalogProductTotal}</StatValue>
                </StatInfo>
              </StatItem>
            )}

            {kpiPerms.canKpiTotalSaleValue && (
              <StatItem data-emphasis>
                <IconContainer sx={iconSx.sales} iconSize="lg">
                  <AttachMoneyIcon />
                </IconContainer>
                <StatInfo>
                  <StatLabel sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>
                    Valor de Venta
                  </StatLabel>
                  <StatValue valueSize="lg">{formatCurrency(totalValue)}</StatValue>
                </StatInfo>
              </StatItem>
            )}

            {kpiPerms.canKpiTotalCostValue && (
              <StatItem data-emphasis>
                <IconContainer sx={iconSx.cost} iconSize="lg">
                  <AttachMoneyIcon />
                </IconContainer>
                <StatInfo>
                  <StatLabel sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>
                    Valor a Costo
                  </StatLabel>
                  <StatValue valueSize="lg">{formatCurrency(totalCostValue ?? 0)}</StatValue>
                </StatInfo>
              </StatItem>
            )}
          </Box>

          {/* Fila 2: ubicación + bajas | disponibilidad venta */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              width: '100%',
              alignItems: 'center',
              borderTop: `1px solid ${theme.palette.divider}`,
              pt: 2,
              mt: 0.5,
              columnGap: { xs: 1, sm: 2 },
              rowGap: 1.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                flex: hasPlacementKpi ? '0 1 auto' : undefined,
                minWidth: 0,
              }}
            >
              {renderPlacementKpis()}
            </Box>
            {availabilityBlock}
          </Box>
        </>
      ) : (
        <>
          {/* Total de piezas */}
          {kpiPerms.canKpiTotalProducts && (
            <StatItem data-compact {...metricProps('')}>
              <IconContainer sx={iconSx.inventory} iconSize="sm">
                <InventoryIcon />
              </IconContainer>
              <StatInfo>
                <StatLabel>Total piezas</StatLabel>
                <StatValue valueSize="sm">
                  {variant === 'warehouse' ? unassignedProducts + inTransitProducts : totalProducts}
                </StatValue>
              </StatInfo>
            </StatItem>
          )}

          {kpiPermissionProfile === 'general' &&
            generalKpiPerms.canKpiCatalogProductTotal &&
            catalogProductTotal !== undefined && (
              <StatItem data-compact>
                <IconContainer sx={iconSx.inventory} iconSize="sm">
                  <AppsIcon />
                </IconContainer>
                <StatInfo>
                  <StatLabel>Total Productos</StatLabel>
                  <StatValue valueSize="sm">{catalogProductTotal}</StatValue>
                </StatInfo>
              </StatItem>
            )}

          {/* Valor de Venta del Inventario */}
          {kpiPerms.canKpiTotalSaleValue && (
            <StatItem data-emphasis>
              <IconContainer sx={iconSx.sales} iconSize="lg">
                <AttachMoneyIcon />
              </IconContainer>
              <StatInfo>
                <StatLabel sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>
                  Valor de Venta
                </StatLabel>
                <StatValue valueSize="lg">{formatCurrency(totalValue)}</StatValue>
              </StatInfo>
            </StatItem>
          )}

          {/* Valor a Costo del inventario */}
          {kpiPerms.canKpiTotalCostValue && (
            <StatItem data-emphasis>
              <IconContainer sx={iconSx.cost} iconSize="lg">
                <AttachMoneyIcon />
              </IconContainer>
              <StatInfo>
                <StatLabel sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>Valor a Costo</StatLabel>
                <StatValue valueSize="lg">{formatCurrency(totalCostValue ?? 0)}</StatValue>
              </StatInfo>
            </StatItem>
          )}

          {variant === 'warehouse' && (
            <>
              {kpiPerms.canKpiUnassigned && (
                <StatItem data-compact {...metricProps('UNASSIGNED')}>
                  <IconContainer sx={iconSx.unassigned} iconSize="sm">
                    <WarehouseIcon />
                  </IconContainer>
                  <StatInfo>
                    <StatLabel>{INV_STATUS_WAREHOUSE_LABEL}</StatLabel>
                    <StatValue valueSize="sm">{unassignedProducts}</StatValue>
                  </StatInfo>
                </StatItem>
              )}

              {kpiPerms.canKpiInTransit && (
                <StatItem data-compact {...metricProps('IN_TRANSIT')}>
                  <IconContainer sx={iconSx.inTransit} iconSize="sm">
                    <LocalShippingIcon />
                  </IconContainer>
                  <StatInfo>
                    <StatLabel>En Tránsito</StatLabel>
                    <StatValue valueSize="sm">{inTransitProducts}</StatValue>
                  </StatInfo>
                </StatItem>
              )}

              {kpiPermissionProfile === 'warehouse' &&
                warehouseKpiPerms.canKpiWarehouseReserved && (
                  <StatItem data-compact {...metricProps('RESERVED')}>
                    <IconContainer sx={iconSx.availability} iconSize="sm">
                      <LockIcon sx={{ color: '#4f46e5' }} />
                    </IconContainer>
                    <StatInfo>
                      <StatLabel>Bloqueadas</StatLabel>
                      <StatValue valueSize="sm">
                        {availabilityStatusMap.get('RESERVED') || 0}
                      </StatValue>
                    </StatInfo>
                  </StatItem>
                )}
            </>
          )}

          {variant === 'branch' && (
            <>
              {kpiPerms.canKpiBajas && (
                <StatItem data-compact {...metricProps('CANCELLED')}>
                  <IconContainer sx={iconSx.availability} iconSize="sm">
                    <HighlightOff sx={{ color: theme.palette.error.main }} />
                  </IconContainer>
                  <StatInfo>
                    <StatLabel>{INV_AVAILABILITY_WRITE_OFF_LABEL_PLURAL}</StatLabel>
                    <StatValue valueSize="sm">{writeOffCount}</StatValue>
                  </StatInfo>
                </StatItem>
              )}
              {availabilityBlock}
            </>
          )}
        </>
      )}
    </StatsContainer>
  );
};

export default InventoryStats;
